import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { apiGet, apiPost } from './api'
import type {
  Club, Program, Coach, Team, TeamsByDivision, Camp, GalleryItem,
  Sponsors, Uniform, ClubStats, SeasonMonth, JamaicaCoach, Testimonial,
  VideoClip, HeroSlide, Division,
  RegistrationPayload, ContactPayload, ScholarshipPayload, DonatePayload,
} from '@/types/api'

export const useClub = () => useQuery({ queryKey: ['club'], queryFn: () => apiGet<Club>('/api/club') })
export const usePrograms = () => useQuery({ queryKey: ['programs'], queryFn: () => apiGet<Program[]>('/api/programs') })
export const useProgram = (slug: string) => useQuery({ queryKey: ['program', slug], queryFn: () => apiGet<Program>(`/api/programs/${slug}`), enabled: !!slug })
export const useTeams = () => useQuery({ queryKey: ['teams'], queryFn: () => apiGet<TeamsByDivision>('/api/teams') })
export const useTeamsDivision = (division: Division) => useQuery({ queryKey: ['teams', division], queryFn: () => apiGet<Team[]>(`/api/teams/${division}`) })
export const useTeam = (division: Division, id: string) => useQuery({ queryKey: ['team', division, id], queryFn: () => apiGet<Team>(`/api/teams/${division}/${id}`), enabled: !!id })
export const useCoaches = () => useQuery({ queryKey: ['coaches'], queryFn: () => apiGet<Coach[]>('/api/coaches') })
export const useCoach = (slug: string) => useQuery({ queryKey: ['coach', slug], queryFn: () => apiGet<Coach>(`/api/coaches/${slug}`), enabled: !!slug })
export const useCamps = () => useQuery({ queryKey: ['camps'], queryFn: () => apiGet<Camp[]>('/api/camps') })
export const useCamp = (slug: string) => useQuery({ queryKey: ['camp', slug], queryFn: () => apiGet<Camp>(`/api/camps/${slug}`), enabled: !!slug })
export const useGallery = () => useQuery({ queryKey: ['gallery'], queryFn: () => apiGet<GalleryItem[]>('/api/gallery') })
export const useSponsors = () => useQuery({ queryKey: ['sponsors'], queryFn: () => apiGet<Sponsors>('/api/sponsors') })
export const useUniforms = () => useQuery({ queryKey: ['uniforms'], queryFn: () => apiGet<Uniform[]>('/api/uniforms') })
export const useStats = () => useQuery({ queryKey: ['stats'], queryFn: () => apiGet<ClubStats>('/api/stats') })
export const useSeasonStats = (teamId: string) => useQuery({ queryKey: ['season', teamId], queryFn: () => apiGet<SeasonMonth[]>(`/api/season-stats/${teamId}`), enabled: !!teamId })
export const useJamaicaCoaches = () => useQuery({ queryKey: ['jamaica-coaches'], queryFn: () => apiGet<JamaicaCoach[]>('/api/jamaica-coaches') })
export const useTestimonials = () => useQuery({ queryKey: ['testimonials'], queryFn: () => apiGet<Testimonial[]>('/api/testimonials') })
export const useVideos = () => useQuery({ queryKey: ['videos'], queryFn: () => apiGet<VideoClip[]>('/api/videos') })
export const useHeroSlides = () => useQuery({ queryKey: ['hero-slides'], queryFn: () => apiGet<HeroSlide[]>('/api/hero-slides') })

export function useSubmitRegistration() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (payload: RegistrationPayload) => apiPost<{ ok: true }, RegistrationPayload>('/api/register', payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['registrations'] }),
  })
}
export function useSubmitContact() {
  return useMutation({
    mutationFn: (payload: ContactPayload) => apiPost<{ ok: true }, ContactPayload>('/api/contact', payload),
  })
}
export function useSubmitScholarship() {
  return useMutation({
    mutationFn: (payload: ScholarshipPayload) => apiPost<{ ok: true }, ScholarshipPayload>('/api/scholarship', payload),
  })
}
export function useCreateDonation() {
  return useMutation({
    mutationFn: (payload: DonatePayload) => apiPost<{ ok: true }, DonatePayload>('/api/donate', payload),
  })
}
